/**
 * kin-cart-refresh.js — cart listener for the Monk "Free Gift & Upsell" app.
 *
 * Monk adds/removes gift lines and discounts server-side, then dispatches a
 * `cart:refresh` event on `document` instead of reloading the page — provided
 * the theme has a listener for it. Without one, Monk's "Auto refresh" setting
 * has to hard-reload the page on every add to cart.
 * See https://help.monkcommerce.com/en/article/why-does-the-websitepage-refresh-when-a-product-is-added-to-cart-hlhrfk/
 *
 * Sleek 2.2.0 ships a listener, but it is broken in two places:
 *
 *   1. `CartDrawer.onCartRefresh` (assets/cart.js) fetches
 *      `${FoxTheme.routes.root_url}?section_id=…`, and
 *      snippets/js-variables.liquid never defines `root_url` — so the request
 *      goes to `/undefined?section_id=…` and the drawer never updates.
 *   2. Nothing listens for `cart:refresh` on the cart page: cart-drawer.liquid
 *      renders nothing when `page_type == 'cart'`, and `MainCart`
 *      (assets/cart.js) only answers `cart:grouped-sections`.
 *
 * This file fixes both without editing theme-owned files, so a Sleek update
 * that rewrites js-variables.liquid or cart.js cannot silently drop the fix.
 */
(() => {
  'use strict';

  const rootUrl = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  // Fix (1): give the theme's own drawer handler the base URL it expects.
  window.FoxTheme = window.FoxTheme || {};
  window.FoxTheme.routes = window.FoxTheme.routes || {};
  if (!window.FoxTheme.routes.root_url) {
    window.FoxTheme.routes.root_url = rootUrl();
  }

  const cartUrl = () => window.FoxTheme.routes.cart_url || `${rootUrl()}cart`;

  let inFlight = false;

  // The theme's components self-report which sections own cart markup on this
  // page (`MainCart`, `CartDrawer`), which is how quantity changes already
  // re-render — reuse it instead of hardcoding a `template--<id>__main-cart`
  // section id that changes with every theme copy.
  const sectionsToRender = () => {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } })
    );
    // Section Rendering API accepts at most 5 ids; the cart page reports one.
    return [...new Set(sections.filter(Boolean))].slice(0, 5);
  };

  const refresh = async () => {
    if (inFlight) return;
    const ids = sectionsToRender();
    if (!ids.length) return;

    inFlight = true;
    try {
      const [sectionsResponse, cartResponse] = await Promise.all([
        fetch(`${window.location.pathname}?sections=${ids.join(',')}`),
        fetch(`${cartUrl()}.js`),
      ]);
      if (!sectionsResponse.ok || !cartResponse.ok) {
        throw new Error(`cart refresh failed: sections ${sectionsResponse.status}, cart ${cartResponse.status}`);
      }
      const [sections, cart] = await Promise.all([sectionsResponse.json(), cartResponse.json()]);

      // Same payload shape the theme publishes after a quantity change, so
      // CartItems.onCartUpdate swaps the markup and <cart-count> follows.
      cart.sections = sections;
      window.FoxTheme.pubsub.publish(window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart });
    } catch (error) {
      console.error('[kin-cart-refresh]', error);
    } finally {
      inFlight = false;
    }
  };

  document.addEventListener('cart:refresh', () => {
    // Fix (2) only: on pages that render <cart-drawer> the theme's own handler
    // (now that root_url resolves) does the work, and running both would
    // double-fetch and race over the same DOM.
    if (document.querySelector('cart-drawer')) return;
    if (!window.FoxTheme.pubsub) return;
    refresh();
  });
})();
